export interface User {
  id: string;
  email: string;
  name: string;
  role: 'SUPERADMIN' | 'ADMIN';
  mustChangePassword?: boolean;
  allowedMenuItems?: string[];
  payrollAccess?: boolean;
  organizationId: string | null;
  organization?: Organization | null;
  // Sat når brugeren har logget ind mindst én gang. Afgør om mail-knappen
  // inviterer en ny bruger eller nulstiller en eksisterendes adgangskode.
  lastLoginAt?: string | null;
  createdAt?: string;
}

// ============ Payroll ============
export type PayrollEmploymentType = 'TIMELOENNET' | 'FULDLOENNET';
export type PayrollManualEntryType = 'SYGDOM' | 'FERIE' | 'FERIEFRIDAG' | 'KURSUS' | 'BARN_SYG' | 'HELLIGDAG_BETALT' | 'ANDET';

export interface PayrollDayBreakdown {
  date: string;
  dayType: 'WEEKDAY' | 'SATURDAY' | 'SUNDAY' | 'HOLIDAY_FULL' | 'HOLIDAY_HALF';
  normalMin: number;
  ot1Min: number;
  ot2Min: number;
  eveAllowanceMin: number;
  nightAllowanceMin: number;
  baseAmount: number;
  ot1Amount: number;
  ot2Amount: number;
  eveAmount: number;
  nightAmount: number;
  manualEntries: Array<{ type: string; hours: number; amount: number }>;
}

export interface PayrollTotals {
  normalHours: number;
  ot1Hours: number;
  ot2Hours: number;
  eveAllowanceHours: number;
  nightAllowanceHours: number;
  baseAmount: number;
  ot1Amount: number;
  ot2Amount: number;
  allowanceAmount: number;
  grossBeforePension: number;
  holidayAccrual: number;
  specialSavings: number;
  pensionEmployer: number;
  pensionEmployee: number;
  totalEmployerCost: number;
}

export interface DatalonPayLine {
  lonartNumber: string;
  name: string;
  sourceKind: string;
  antal: number;
  rate: number;
  belob: number;
}

export interface PayrollBreakdown {
  driverId: string;
  driverName: string;
  periodStart: string;
  periodEnd: string;
  ruleSetId: string;
  ruleSetName: string;
  rateRowValidFrom: string;
  employmentType: PayrollEmploymentType;
  days: PayrollDayBreakdown[];
  totals: PayrollTotals;
  warnings: string[];
  payable?: { lines: DatalonPayLine[]; total: number };
}

export interface PayrollRuleSet {
  id: string;
  organizationId: string;
  name: string;
  catalogKey: string | null;
  isCloned: boolean;
  isDefault: boolean;
  ruleOverrides: Record<string, any>;
  rateOverrides: PayrollRate[];
  _count?: { profiles: number };
  createdAt: string;
  updatedAt: string;
}

export interface PayrollRate {
  id: string;
  validFrom: string;
  baseHourly: string | number;
  driverSupplement: string | number;
  seniorityBonus: string | number;
  ot1Supplement: string | number;
  ot2Supplement: string | number;
  eveningAllowance: string | number;
  nightAllowance: string | number;
  holidayAccrualPct: string | number;
  specialSavingsPct: string | number;
  pensionEmployerPct: string | number;
  pensionEmployeePct: string | number;
}

export interface PayrollCatalog {
  id: string;
  key: string;
  name: string;
  description: string | null;
  version: string;
  publishedAt: string;
  ruleConfig: Record<string, any>;
  rates: PayrollRate[];
}

export interface DriverPayrollProfile {
  id: string;
  organizationId: string;
  driverId: string;
  driverName: string;
  ruleSetId: string;
  ruleSet?: { id: string; name: string };
  employmentType: PayrollEmploymentType;
  employmentStart: string;
  monthlySalary: string | number | null;
  weeklyContractHours: string | number;
  pensionOptIn: boolean;
  notes: string | null;
}

export interface PayrollManualEntry {
  id: string;
  organizationId: string;
  driverId: string;
  driverName: string;
  date: string;
  type: PayrollManualEntryType;
  hours: string | number;
  amountOverride: string | number | null;
  note: string | null;
  createdBy: string;
  createdAt: string;
}

export interface PayrollPeriodSnapshot {
  id: string;
  organizationId: string;
  driverId: string;
  periodStart: string;
  periodEnd: string;
  periodType: 'WEEK' | 'MONTH';
  ruleSetId: string;
  breakdown: PayrollBreakdown;
  totalGross: string | number;
  lockedAt: string | null;
  lockedBy: string | null;
}

export type DoraApiEnvironment = 'PRODUCTION' | 'STAGING';

export type OrganizationLanguage = 'da' | 'sv' | 'no' | 'en';

export interface Organization {
  id: string;
  name: string;
  cvr: string;
  address?: string;
  city?: string;
  postalCode?: string;
  doraApiKey?: string;
  doraApiEnvironment: DoraApiEnvironment;
  language: OrganizationLanguage;
  fmsApiKey?: string;
  fmsCustomerId?: string;
  // Emballagekompensation. Kommer med i /auth/login og /auth/me, så menupunktet
  // kan skjules for de organisationer der ikke er affaldsindsamlere.
  emballageEnabled?: boolean;
  createdAt: string;
  updatedAt: string;
  userCount?: number;
}

export interface OrgApiKey {
  id: string;
  name: string;
  keyPrefix: string;
  scope: string;
  lastUsedAt: string | null;
  createdAt: string;
  revokedAt: string | null;
}

export interface OrgApiKeyCreated extends OrgApiKey {
  key: string; // Den klare nøgle — vises KUN ved oprettelse
}

export interface InviteCredentials {
  user: {
    id: string;
    email: string;
    name: string;
    role: string;
  };
  credentials: {
    email: string;
    password: string;
  };
  organization: {
    id: string;
    name: string;
  };
}

export interface Trailer {
  id: string;
  number: string;
  type: string;
  lm: string;
  height: string;
  width: string;
  length: string;
  weight: string | null;
  volume: string | null;
  powered: boolean;
  qualities: string[] | null;
  fuelType: string | null;
  vehicleType: string;
  kmPerLiter: string | null;
  co2EmissionsClass: string | null;
  teams: string[];
  alias: string | null;
  color: string | null;
  note: string | null;
}

export interface TrailerEconomics {
  trailerId: string;
  trailerNumber: string;
  trailerType: string;
  revenue: number;
  kilometers: number;
  fuelConsumption: number;
  fuelCost: number;
  manualCosts: number;
  costRuleCosts: number;
  productSalesCost: number;
  totalCosts: number;
  profit: number;
  revenuePerKm: number;
  routeCount: number;
  cargoCount: number;
}

export interface PeriodSummary {
  totalRevenue: number;
  totalKilometers: number;
  totalFuelConsumption: number;
  totalFuelCost: number;
  totalManualCosts: number;
  totalCostRuleCosts: number;
  totalProductSalesCost: number;
  totalProfit: number;
  averageRevenuePerKm: number;
  trailerCount: number;
  routeCount: number;
  cargoCount: number;
  utilizationRate: number;
}

export interface CalendarEntry {
  date: string;
  trailerId: string;
  trailerNumber: string;
  isActive: boolean;
  routeCount: number;
  cargoCount: number;
  revenue: number;
  kilometers: number;
}

export interface ManualCost {
  id: string;
  organizationId: string;
  trailerId: string;
  routeId?: string | null;
  driverIds?: string[];
  date: string;
  amount: number;
  description: string;
  category: string;
  supplierId?: string | null;
  createdAt: string;
}

export interface CostCategoryItem {
  id: string;
  organizationId: string;
  key: string;
  name: string;
  color: string;
  sortOrder: number;
  createdAt: string;
}

export interface Supplier {
  id: string;
  organizationId: string;
  name: string;
  cvr: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CvrCompanyInfo {
  cvr: string;
  name: string;
  address: string;
  city: string;
  postalCode: string;
  phone?: string;
  email?: string;
}

export interface Route {
  id: string;
  routeName: string | null;
  firstStop: { city: string | null };
  lastStop: { city: string | null };
  cargoOrder: string[];
  createdAt: string;
  destinationDate: string | null;
  trailerId: string | null;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface DateRange {
  from: Date;
  to: Date;
}

export type Period = 'day' | 'week' | 'month' | 'quarter' | 'year' | 'custom';

// Cargo Calendar Types
export interface CargoStop {
  city: string | null;
  postcode: string | null;
  countryCode: string | null;
  date: string;
  time: string;
  place: string | null;
}

export interface CalendarCargoEntry {
  id: string;
  ref: string;
  type: string;
  description: string;
  note: string | null;
  customerNumber: string | null;
  customerName: string | null;
  invoiceRef: string | null;
  adr: boolean;
  adrText: string | null;
  qualities: string[];
  requirements: string[];
  pickup: CargoStop;
  delivery: CargoStop | null;
}

export interface TrailerCalendarDay {
  date: string;
  cargos: CalendarCargoEntry[];
}

export interface TrailerCalendarData {
  trailerId: string;
  trailerNumber: string;
  trailerAlias: string | null;
  trailerType: string;
  color: string | null;
  teams: string[]; // Team IDs
  days: TrailerCalendarDay[];
}

export interface TeamInfo {
  id: string;
  name: string;
}

export interface CargoCalendarResponse {
  period: { from: string; to: string };
  trailers: TrailerCalendarData[];
  teams: TeamInfo[];
  stats: {
    trailerCount: number;
    totalCargos: number;
    activeDays: number;
    totalDays: number;
    utilizationRate: number;
  };
}

// e-conomic Integration Types
export interface EconomicInvoice {
  id: string;
  organizationId: string;
  economicId: string;
  supplierNumber: number;
  supplierName: string;
  invoiceNumber: string | null;
  date: string;
  amount: number;
  currency: string;
  text: string | null;
  accountNumber: number | null;
  trailerId: string | null;
  routeId: string | null;
  cargoId: string | null;
  costCategoryKey: string | null;
  suggestedTrailerId: string | null;
  matchStatus: 'UNMATCHED' | 'SUGGESTED' | 'CONFIRMED' | 'IGNORED';
  syncedAt: string;
}

export interface EconomicJournalEntry {
  id: string;
  journalNumber: number;
  voucherNumber: number | null;
  status: 'DRAFT' | 'POSTED' | 'FAILED';
  manualCostId: string;
  amount: number;
  description: string;
  createdAt: string;
  errorMessage: string | null;
}

export interface EconomicSupplier {
  supplierNumber: number;
  name: string;
  currency: string;
}

// Organization Settings Types
export interface OrganizationSettings {
  id: string;
  organizationId: string;
  companyName: string | null;
  companyPhone: string | null;
  companyPhone2: string | null;
  companyEmail: string | null;
  companyCvr: string | null;
  companyAddress: string | null;
  companyCity: string | null;
  companyPostalCode: string | null;
  companyCountry: string | null;
  companyLogoUrl: string | null;
  companyLogoMime: string | null; // MIME type of uploaded logo
  termsLinks: string[];
  autoUploadToDora: boolean;
  autoUploadFromDate: string | null;
  autoUploadTemplates: string[];
  defaultTemplateId: string | null;
  nextKoreseddelNumber: number;
  splitRouteTeamId: string | null;
  danlonSalaryType: string | null;
  datalonVirksomhedsNr: string | null;
  datalonLoentermin: string | null;
  fmsApiKey: string | null;
  fmsCustomerId: string | null;
  costAllocationMethod: string | null;
  economicAgreementToken?: string;
  economicDefaultJournal?: number | null;
  economicWebhookSecret?: string | null;
  createdAt: string;
  updatedAt: string;
}

// Driver mapping for Danløn + FMS integration
export interface DriverMapping {
  driverId: string;
  driverName: string;
  employeeNumber: string;        // Danløn medarbejdernummer
  datalonEmployeeNumber: string; // Dataløn Branche medarbejdernummer (kan afvige)
  fmsDeviceId: string;
}

// FMS Device/Tracker
export interface FmsDevice {
  id: string;
  name: string;
  imei: string;
  status: string;
  lastSeen: string | null;
  vehicleRegistration: string | null;
}

// Team type
export interface DoraTeam {
  id: string;
  name: string;
}

// Køreseddel Types
export interface KoreseddelTemplate {
  id: string;
  name: string;
  description: string;
  sections: {
    header: boolean;
    cargoInfo: boolean;
    addresses: 'basic' | 'detailed' | 'all-stops';
    invoiceLines: boolean;
    showAmounts: boolean;
    timeTracking: boolean | 'detailed';
    notes: boolean;
  };
  layout: 'single-column' | 'two-column';
}

export interface GeneratedKoreseddel {
  id: string;
  organizationId: string;
  cargoId: string;
  cargoRef: string;
  templateId: string;
  templateName?: string;
  koreseddelNumber: number;
  filename: string;
  uploadedToDora: boolean;
  uploadedAt: string | null;
  doraDocumentId: string | null;
  generatedAt: string;
}

export interface KoreseddelCargo {
  id: string;
  ref: string;
  type: string;
  description: string;
  note: string | null;
  customerName: string | null;
  customerNumber: string | null;
  invoiceRef: string | null;
  firstStop: CargoStop;
  lastStop: CargoStop | null;
  invoiceLineCount: number;
  documentCount: number;
  hasTimeTracking: boolean;
  hasKoreseddel: boolean;
  koreseddelInfo: {
    id: string;
    cargoId: string;
    templateId: string;
    generatedAt: string;
    uploadedToDora: boolean;
  } | null;
}

export interface BulkGenerateResult {
  generated: number;
  failed: number;
  results: Array<{
    cargoId: string;
    cargoRef: string;
    success: boolean;
    koreseddelId?: string;
    error?: string;
  }>;
}

export interface BulkUploadResult {
  uploaded: number;
  failed: number;
  results: Array<{
    koreseddelId: string;
    success: boolean;
    error?: string;
  }>;
}

// Bulk Upload Job Types (Retroactive upload)
export interface BulkUploadJobError {
  cargoId: string;
  cargoRef: string;
  error: string;
}

export interface BulkUploadJob {
  id: string;
  organizationId: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  templateId: string;
  fromDate: string;
  total: number;
  processed: number;
  failed: number;
  errors: BulkUploadJobError[];
  startedAt: string;
  completedAt: string | null;
}

// Trailer Cargo (for trailer detail view)
export interface TrailerCargoStop {
  city: string | null;
  postcode: string | null;
  countryCode: string | null;
  date: string;
  time: string;
  place: string | null;
}

export interface TrailerCargo {
  id: string;
  ref: string;
  type: string;
  description: string;
  customerName: string | null;
  customerNumber: string | null;
  pickup: TrailerCargoStop | null;
  delivery: TrailerCargoStop | null;
  routeId: string;
  routeName: string | null;
  destinationDate: string | null;
  revenue: number;
}

// Route Overview Types
export interface RouteCargoSummary {
  id: string;
  ref: string;
  type: string;
  description: string | null;
  customerName: string | null;
  note: string | null;
  firstStop: { city: string | null; date: string };
  lastStop: { city: string | null; date: string };
  isDelivered?: boolean; // Whether the last stop is completed
}

export interface RouteWithDetails {
  id: string;
  routeName: string | null;
  firstStop: { city: string | null };
  lastStop: { city: string | null };
  destinationDate: string | null;
  createdAt: string;
  trailerId: string;
  trailerNumber: string;
  trailerAlias: string | null;
  cargoCount: number;
  cargos: RouteCargoSummary[];
}

// Driver Types
export interface Driver {
  id: string;
  firstName: string;
  lastName: string;
}

// Cost Rule Types
export type CostRuleType =
  | 'PER_KM'
  | 'PER_DAY'
  | 'PER_TRIP'
  | 'PER_HOUR'
  | 'PER_INVOICE_LINE'
  | 'FIXED_MONTHLY';

export interface CostRule {
  id: string;
  organizationId: string;
  name: string;
  description: string | null;
  ruleType: CostRuleType;
  category: string;
  rate: number;
  trailerIds: string[];
  driverIds: string[];
  productNumbers: string[];
  supplierId: string | null;
  supplierName: string | null;
  isActive: boolean;
  validFrom: string | null;
  validTo: string | null;
  createdAt: string;
  updatedAt: string;
}

// Work activities (pause/værksted/vask logget af chauffør-app via X-API-Key)
export type WorkActivityKind = 'BREAK' | 'WORKSHOP' | 'WASHING' | 'OTHER';

export interface WorkActivityEntry {
  id: string;
  organizationId: string;
  driverId: string;
  driverName: string;
  date: string;
  kind: WorkActivityKind;
  customLabel: string | null;
  trailerId: string | null;
  trailerNumber: string | null;
  startedAt: string;
  endedAt: string | null;
  minutes: number | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

// Work Hours Types
export interface WorkHourEntry {
  id: string;
  routeId: string;
  trailerId: string;
  trailerNumber: string;
  trailerAlias: string | null;
  driverId: string;
  driverName: string;
  date: string;
  source?: 'WORKDAY' | 'DORA_TMS';
  manuallyEdited?: boolean;
  started: {
    dateTime: string;
    odometer: string;
    note: string | null;
  } | null;
  stopped: {
    dateTime: string;
    odometer: string;
    note: string | null;
  } | null;
  // Time tracking
  pauseMinutes: number;
  totalMinutes: number | null;
  workMinutes: number | null;
  // Per-cargo time tracking details
  cargoDetails?: Array<{
    cargoId: string;
    cargoRef: string;
    cargoType: string | null;
    customerName: string | null;
    startedAt: string | null;
    endedAt: string | null;
    pauseMinutes: number;
    totalMinutes: number | null;
    workMinutes: number | null;
  }>;
}

export interface UpdateTripData {
  driverId: string;
  pauseMinutes?: number;
  start?: {
    dateTime: string;
    odometer: string;
    note?: string | null;
  };
  end?: {
    dateTime: string;
    odometer: string;
    note?: string | null;
  };
}

// Route Split Types
// Multi-dimensional Economics Types
export interface RouteEconomics {
  routeId: string;
  routeRef: string;
  date: string;
  trailerId: string;
  trailerNumber: string;
  driverNames: string;
  firstCity: string;
  lastCity: string;
  cargoCount: number;
  revenue: number;
  kilometers: number;
  hours: number;
  revenuePerKm: number;
  productSalesCost: number;
  profit: number;
}

export interface DriverEconomics {
  driverId: string;
  driverName: string;
  routeCount: number;
  cargoCount: number;
  revenue: number;
  kilometers: number;
  hours: number;
  revenuePerKm: number;
  costRuleCosts: number;
}

export interface CustomerEconomics {
  customerNumber: string;
  customerName: string;
  cargoCount: number;
  revenue: number;
  productSalesCost: number;
  profit: number;
}

export interface OrderEconomics {
  cargoId: string;
  cargoRef: string;
  date: string;
  customerName: string;
  customerNumber: string;
  firstCity: string;
  lastCity: string;
  invoiceLineCount: number;
  revenue: number;
  routeId: string | null;
  trailerId: string | null;
  trailerNumber: string | null;
  loadmeters: number;
  orderKm: number;
  allocatedCost: number;
  productSalesCost: number;
  profit: number;
  allocationPct: number;
}

export interface SplitRouteResult {
  routeId: string;  // Original route - now has 2/3 orders
  originalCargoCount: number;
  splitCargoCount: number;
  route1Id: string;  // New route for 1/3 orders (originals)
  route3Id: string | null;  // Route for 3/3 orders (if not undispatched)
  splitResults: Array<{
    originalCargoId: string;
    originalRef: string;
    cargo2: { id: string; ref: string };  // 2/3 (pickup→reload)
    cargo3: { id: string; ref: string };  // 3/3 (reload→delivery)
  }>;
}

// FMS Comparison Types
export interface FmsComparisonEntry {
  firstSeen: string | null;
  lastSeen: string | null;
  drivingMinutes: number;
  distanceKm: number;
  journeyCount: number;
}

export type FmsComparisonData = Record<string, FmsComparisonEntry>;

// Price Calculator Types
export interface PriceCalculatorWaypoint {
  lat: number;
  lon: number;
}

export interface PriceEstimateRequest {
  waypoints: PriceCalculatorWaypoint[];
  trailerId?: string;
  driverId?: string;
  overrideKm?: number;
  overrideHours?: number;
  emptyKm: number;
  emptyHours: number;
  calendarDays: number;
  routeCount: number;
  invoiceLineCount: number;
}

export interface PriceIntelligentRequest extends PriceEstimateRequest {
  customerNumber?: string;
  lookbackDays: number;
}

export interface ComputedCost {
  ruleId: string;
  ruleName: string;
  ruleType: CostRuleType;
  category: string;
  trailerIds: string[];
  driverIds: string[];
  supplierId: string | null;
  rate: number;
  units: number;
  unitLabel: string;
  amount: number;
}

export interface PriceRouteInfo {
  distanceKm: number;
  durationHours: number;
  emptyKm: number;
  emptyHours: number;
  totalKm: number;
  totalHours: number;
  googleDistanceKm: number;
  googleDurationHours: number;
}

export interface PriceEstimateResponse {
  route: PriceRouteInfo;
  costBreakdown: ComputedCost[];
  totalCost: number;
}

export interface HistoricalSimilarRoutes {
  count: number;
  avgRevenue: number;
  avgKm: number;
  avgHours: number;
  avgRevenuePerKm: number;
  minRevenue: number;
  maxRevenue: number;
  percentile90Revenue: number;
}

export interface CustomerBenchmark {
  customerName: string;
  avgRevenuePerOrder: number;
  orderCount: number;
}

export interface SuggestedPriceRange {
  low: number;
  recommended: number;
  high: number;
  basis: string;
}

export interface PriceIntelligentResponse extends PriceEstimateResponse {
  historicalSimilarRoutes: HistoricalSimilarRoutes | null;
  customerBenchmark: CustomerBenchmark | null;
  suggestedPriceRange: SuggestedPriceRange | null;
}

// Route Detail Types
export interface RouteDetailCargo {
  cargoId: string;
  cargoRef: string;
  customerName: string;
  firstCity: string;
  lastCity: string;
  invoiceLines: Array<{
    id: string;
    description: string;
    productNumber: string | null;
    amount: number;
    quantity: number;
    discount: number;
    total: number;
  }>;
  revenue: number;
}

export interface RouteDetailCostRule {
  ruleId: string;
  ruleName: string;
  ruleType: string;
  category: string;
  supplierId: string | null;
  rate: number;
  units: number;
  unitLabel: string;
  amount: number;
}

export interface RouteDetailManualCost {
  id: string;
  date: string;
  amount: number;
  description: string;
  category: string;
  supplierId: string | null;
  supplierName: string | null;
}

export interface RouteDetailData {
  routeId: string;
  routeRef: string;
  date: string;
  trailerId: string;
  trailerNumber: string;
  driverNames: string;
  driverIds: string[];
  firstCity: string;
  lastCity: string;
  revenue: number;
  kilometers: number;
  hours: number;
  revenuePerKm: number;
  cargos: RouteDetailCargo[];
  costRuleBreakdown: RouteDetailCostRule[];
  costRuleCosts: number;
  manualCosts: RouteDetailManualCost[];
  manualCostsTotal: number;
  totalCosts: number;
  profit: number;
}

// CMR types
export interface CmrFieldDefinition {
  fieldNumber: number;
  name: string;
  description: string;
}

export interface CmrFieldMapping {
  fieldNumber: number;
  sourceType: 'CARGO' | 'ORGANIZATION' | 'STATIC' | 'EMPTY';
  sourceField: string | null;
  staticText: string | null;
}

export interface CmrAvailableFields {
  CARGO: Array<{ key: string; label: string }>;
  ORGANIZATION: Array<{ key: string; label: string }>;
}

export interface CmrFieldsResponse {
  fields: CmrFieldDefinition[];
  availableFields: CmrAvailableFields;
}

export interface GeneratedCMR {
  id: string;
  cargoId: string;
  cargoRef: string;
  cmrNumber: number;
  filename: string;
  uploadedToDora: boolean;
  generatedAt: string;
}

export interface CmrBulkGenerateResult {
  succeeded: number;
  failed: number;
  results: Array<{
    cargoId: string;
    cmrId: string;
    cargoRef: string;
    success: boolean;
    error?: string;
  }>;
}

// Quote Types
export type QuoteStatus = 'DRAFT' | 'SENT' | 'ACCEPTED' | 'REJECTED' | 'CONVERTED' | 'EXPIRED';

export interface QuoteStopPlace {
  placeName?: string | null;
  address?: string;
  postcode?: string;
  city?: string | null;
  country?: string;
}

export interface QuoteStop {
  loadmeters?: string;
  date: string;
  time?: string | null;
  weight?: string;
  height?: string;
  width?: string;
  length?: string;
  cubicMeters?: string;
  ref?: string | null;
  contact?: string | null;
  contactPhone?: string | null;
  pieces?: Array<{ unit: string; amount: number }>;
  place?: QuoteStopPlace;
  salesLocationId?: string | null;
}

export interface QuotePriceLine {
  description: string;
  amount: number;
  quantity: number;
  unitName?: string | null;
  productId?: string | null;
  productNumber?: string | null;
  discountPercentage?: string | null;
  loadId?: string | null;
}

export type QuoteType = 'TRANSPORT' | 'CONTAINER' | 'SERVICE' | 'WASTE' | 'RECURRING';

export interface QuoteLoad {
  id?: string;
  cargoType: string | null;
  description: string | null;
  description2: string | null;
  note: string | null;
  adr: boolean;
  adrText: string | null;
  minTemperature: number | null;
  maxTemperature: number | null;
  teamId: string | null;
  labelId: string | null;
  requirements: string[];
  qualities: string[];
  pickups: QuoteStop[];
  dropoffs: QuoteStop[];
}

// NY adresse-model: én tilbudsadresse + N leveringer TIL + M bortkørsler FRA.
// Tilbudsadressen kan også selv have varer (fx kranarbejde uden flytning af gods).
export interface QuoteAddress {
  salesLocationId?: string | null;
  placeName?: string | null;
  address?: string;
  postcode?: string;
  city?: string | null;
  country?: string;
  contact?: string | null;
  contactPhone?: string | null;
  ref?: string | null;
  date?: string;
  time?: string | null;
  // Varer der udføres AT tilbudsadressen (uden flytning). Bliver til én DORA-cargo
  // med pickup=dropoff=tilbudsadressen + én invoice-line pr. vare.
  items?: QuoteMovementItem[];
}

// Synligheds-config — pr. felt på kunde-PDF. Default: kun tilbudsadresse, datoer og varelinjer er synlige.
export interface QuoteVisibility {
  showQuoteAddress: boolean;
  showQuoteAddressContact: boolean;
  showQuoteAddressRef: boolean;
  showMovementAddresses: boolean;
  showDates: boolean;
  showItems: boolean;
  showItemPrices: boolean;
  showCargoType: boolean;
  showLineDescription: boolean;
  showWeight: boolean;
  showLoadmeters: boolean;
  showLineRef: boolean;
  showLineContact: boolean;
  showCustomerNumber: boolean;
}

export const DEFAULT_QUOTE_VISIBILITY: QuoteVisibility = {
  showQuoteAddress: true,
  showQuoteAddressContact: false,
  showQuoteAddressRef: false,
  showMovementAddresses: false,
  showDates: true,
  showItems: true,
  showItemPrices: true,
  showCargoType: false,
  showLineDescription: false,
  showWeight: false,
  showLoadmeters: false,
  showLineRef: false,
  showLineContact: false,
  showCustomerNumber: false,
};

export interface QuoteMovementItem {
  id: string;
  productId?: string | null;     // DORA product id (fra /products)
  productNumber?: string | null;  // DORA productNumber
  productName?: string | null;    // Produktnavn (vises i UI; kopieres til description hvis tom)
  description?: string | null;    // Faktura-tekst (default = productName)
  quantity: number;
  unitName?: string | null;
  unitPrice: number;
  // Intern kommentar pr. vare. Vises ikke til kunden; sendes med til DORA's
  // cargo-note ved accept/konvertering.
  internalNote?: string | null;
}

export interface QuoteMovementLine {
  id?: string;
  // Ekstern adresse (afhentnings- eller bortkørselsadresse)
  salesLocationId?: string | null;
  placeName?: string | null;
  address?: string;
  postcode?: string;
  city?: string | null;
  country?: string;
  contact?: string | null;
  contactPhone?: string | null;
  // Timing
  date?: string;
  time?: string | null;
  // Gods
  cargoType?: string | null;
  description?: string | null;
  description2?: string | null;
  note?: string | null;
  ref?: string | null;
  weight?: string;
  loadmeters?: string;
  adr?: boolean;
  adrText?: string | null;
  minTemperature?: number | null;
  maxTemperature?: number | null;
  teamId?: string | null;
  labelId?: string | null;
  requirements?: string[];
  qualities?: string[];
  // Varer pr. linje — hver vare bliver til en fakturalinje på den DORA-cargo ved convert
  items?: QuoteMovementItem[];
}

export interface Quote {
  id: string;
  organizationId: string;
  quoteNumber: number;
  status: QuoteStatus;
  quoteType: QuoteType;
  recurringPattern: string | null;
  recurringWeekdays: number[];
  recurringIntervalDays: number | null;
  recurringEndDate: string | null;
  doraScheduleId: string | null;
  lastFollowupAt: string | null;
  followupCount: number;
  rejectedReason: string | null;
  internalNote: string | null;
  customerName: string;
  customerEmail: string;
  customerPhone: string | null;
  customerNumber: string | null;
  cargoType: string | null;
  description: string | null;
  description2: string | null;
  note: string | null;
  adr: boolean;
  adrText: string | null;
  minTemperature: number | null;
  maxTemperature: number | null;
  teamId: string | null;
  labelId: string | null;
  requirements: string[];
  qualities: string[];
  pickups: QuoteStop[];
  dropoffs: QuoteStop[];
  loads: QuoteLoad[];
  // NY adressestruktur (kun udfyldt når useNewAddressModel=true)
  useNewAddressModel: boolean;
  quoteAddress: QuoteAddress | null;
  pickupLines: QuoteMovementLine[];
  dropoffLines: QuoteMovementLine[];
  priceLines: QuotePriceLine[];
  totalAmount: number;
  currency: string;
  validUntil: string | null;
  terms: string | null;
  customerMessage: string | null;
  hidePickupsFromCustomer: boolean;
  hideDropoffsFromCustomer: boolean;
  hideCustomerNumberOnPdf: boolean;
  reminderEnabled: boolean;
  reminderIntervalsHours: number[];
  reminderSentIntervals: number[];
  acceptToken: string;
  sentAt: string | null;
  acceptedAt: string | null;
  rejectedAt: string | null;
  convertedAt: string | null;
  doraCargoId: string | null;
  doraCargoRef: string | null;
  doraCargoTemplateIds: string[];
  customerComment: string | null;
  createdByUserId: string;
  createdBy?: { id: string; name: string; email: string } | null;
  createdAt: string;
  updatedAt: string;
}

export type QuoteActivityKind = 'NOTE' | 'CALL' | 'EMAIL' | 'MEETING' | 'OTHER';

export interface QuoteActivity {
  id: string;
  quoteId: string;
  userId: string;
  user?: { name: string | null; email: string } | null;
  kind: QuoteActivityKind;
  body: string;
  createdAt: string;
}

export interface QuoteCreateInput {
  customerName: string;
  customerEmail: string;
  customerPhone?: string | null;
  customerNumber?: string | null;
  quoteType?: QuoteType;
  recurringPattern?: string | null;
  recurringWeekdays?: number[];
  recurringIntervalDays?: number | null;
  recurringEndDate?: string | null;
  cargoType?: string | null;
  description?: string | null;
  description2?: string | null;
  note?: string | null;
  adr?: boolean;
  adrText?: string | null;
  minTemperature?: number | null;
  maxTemperature?: number | null;
  teamId?: string | null;
  labelId?: string | null;
  requirements?: string[];
  qualities?: string[];
  pickups: QuoteStop[];
  dropoffs: QuoteStop[];
  loads?: QuoteLoad[];
  // Sæt useNewAddressModel=true og udfyld quoteAddress + pickupLines/dropoffLines for ny model
  useNewAddressModel?: boolean;
  quoteAddress?: QuoteAddress | null;
  pickupLines?: QuoteMovementLine[];
  dropoffLines?: QuoteMovementLine[];
  priceLines?: QuotePriceLine[];
  totalAmount?: number;
  currency?: string;
  validUntil?: string | null;
  terms?: string | null;
  internalNote?: string | null;
  customerMessage?: string | null;
  hidePickupsFromCustomer?: boolean;
  hideDropoffsFromCustomer?: boolean;
  hideCustomerNumberOnPdf?: boolean;
  reminderEnabled?: boolean;
  reminderIntervalsHours?: number[];
}

export interface PublicQuoteView {
  id: string;
  quoteNumber: number;
  status: QuoteStatus;
  customerName: string;
  customerEmail: string;
  customerPhone: string | null;
  cargoType: string | null;
  description: string | null;
  description2: string | null;
  note: string | null;
  adr: boolean;
  adrText: string | null;
  minTemperature: number | null;
  maxTemperature: number | null;
  requirements: string[];
  qualities: string[];
  pickups: QuoteStop[];
  dropoffs: QuoteStop[];
  loads: QuoteLoad[];
  priceLines: QuotePriceLine[];
  totalAmount: number;
  currency: string;
  validUntil: string | null;
  terms: string | null;
  sentAt: string | null;
  acceptedAt: string | null;
  rejectedAt: string | null;
  createdAt: string;
  customerMessage: string | null;
  hidePickupsFromCustomer: boolean;
  hideDropoffsFromCustomer: boolean;
  company: {
    name: string;
    address: string | null;
    postalCode: string | null;
    city: string | null;
    phone: string | null;
    email: string | null;
    cvr: string | null;
  };
}

// --- Order Schedules (Ordreskemaer) ---

export type OrderScheduleStatus = 'ACTIVE' | 'PAUSED' | 'COMPLETED';
export type RecurrenceType = 'DAILY' | 'WEEKLY' | 'BIWEEKLY' | 'EVERY_X_DAYS' | 'SPECIFIC_WEEKDAYS';

export interface StopTemplate {
  dateOffset: number;
  time?: string | null;
  openingTime?: string | null;
  loadmeters?: string;
  height?: string | null;
  width?: string | null;
  length?: string | null;
  weight?: string | null;
  cubicMeters?: string | null;
  ref?: string | null;
  contact?: string | null;
  contactPhone?: string | null;
  tag?: string | null;
  place?: QuoteStopPlace;
  pieces?: Array<{ unit: string; amount: number }>;
}

export interface InvoiceLineTemplate {
  description: string;
  amount: string;
  quantity: string;
  unitName?: string | null;
  productId?: string | null;
  productNumber?: string | null;
  discountPercentage?: string | null;
}

export interface ScheduledCargo {
  id: string;
  templateId: string;
  scheduledDate: string;
  doraCargoId: string | null;
  doraCargoRef: string | null;
  status: 'PENDING' | 'CREATED' | 'UPDATED' | 'FAILED';
  doraInvoiceLineIds: string[];
  errorMessage: string | null;
  createdInDoraAt: string | null;
  lastUpdatedAt: string | null;
  createdAt: string;
}

export interface OrderTemplate {
  id: string;
  scheduleId: string;
  name: string;
  startDate: string | null;
  customerNumber: string | null;
  customerName: string | null;
  cargoType: string | null;
  description: string | null;
  description2: string | null;
  note: string | null;
  invoiceRef: string | null;
  adr: boolean;
  adrText: string | null;
  teamId: string | null;
  labelId: string | null;
  requirements: string[];
  qualities: string[];
  pickupTemplate: StopTemplate[];
  dropoffTemplate: StopTemplate[];
  invoiceLines: InvoiceLineTemplate[];
  createdAt: string;
  updatedAt: string;
  generatedCargos?: ScheduledCargo[];
  _count?: { generatedCargos: number };
}

export interface OrderSchedule {
  id: string;
  organizationId: string;
  name: string;
  status: OrderScheduleStatus;
  recurrenceType: RecurrenceType;
  intervalDays: number | null;
  weekdays: number[];
  weekdaysOnly: boolean;
  startDate: string;
  endDate: string;
  leadTimeDays: number;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  orderTemplates?: OrderTemplate[];
  _count?: { orderTemplates: number };
}

export interface OrderScheduleInput {
  name: string;
  recurrenceType: RecurrenceType;
  intervalDays?: number | null;
  weekdays?: number[];
  weekdaysOnly?: boolean;
  startDate: string;
  endDate: string;
  leadTimeDays?: number;
}

export interface OrderTemplateInput {
  name: string;
  startDate?: string | null;
  customerNumber?: string | null;
  customerName?: string | null;
  cargoType?: string | null;
  description?: string | null;
  description2?: string | null;
  note?: string | null;
  invoiceRef?: string | null;
  adr?: boolean;
  adrText?: string | null;
  teamId?: string | null;
  labelId?: string | null;
  requirements?: string[];
  qualities?: string[];
  pickupTemplate: StopTemplate[];
  dropoffTemplate: StopTemplate[];
  invoiceLines?: InvoiceLineTemplate[];
}

// --- Produktsalg / SalesLocation ---

export interface SalesLocationProduct {
  id: string;
  salesLocationId: string;
  doraProductId: string | null;
  doraProductNumber: string;
  doraProductName: string;
  price: number | null;
  costPrice: number | null;
  unitName: string | null;
  autoTriggerDoraProductId: string | null;
  autoTriggerProductNumber: string | null;
  autoTriggerProductName: string | null;
  autoTriggerFactor: number | null;
  autoTriggerPrice: number | null;
  autoTriggerCostPrice: number | null;
  autoTriggerUnitName: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SalesLocation {
  id: string;
  organizationId: string;
  name: string;
  type: string;
  address: string | null;
  postcode: string | null;
  city: string | null;
  country: string;
  placeName: string | null;
  lat: number | null;
  lon: number | null;
  active: boolean;
  note: string | null;
  products: SalesLocationProduct[];
  createdAt: string;
  updatedAt: string;
}

export interface SalesLocationInput {
  name: string;
  type?: string;
  address?: string | null;
  postcode?: string | null;
  city?: string | null;
  country?: string;
  placeName?: string | null;
  lat?: number | null;
  lon?: number | null;
  active?: boolean;
  note?: string | null;
}

export interface SalesLocationProductInput {
  doraProductId: string | null;
  doraProductNumber: string;
  doraProductName: string;
  price?: number | null;
  costPrice?: number | null;
  unitName?: string | null;
  autoTriggerDoraProductId?: string | null;
  autoTriggerProductNumber?: string | null;
  autoTriggerProductName?: string | null;
  autoTriggerFactor?: number | null;
  autoTriggerPrice?: number | null;
  autoTriggerCostPrice?: number | null;
  autoTriggerUnitName?: string | null;
  active?: boolean;
}

export interface PriceGroup {
  id: string;
  organizationId: string;
  name: string;
  description: string | null;
  discountPercent: number | null;
  validFrom: string | null;
  validUntil: string | null;
  customerNumbers: string[];
  prices?: PriceGroupPrice[];
  _count?: { prices: number };
  createdAt: string;
  updatedAt: string;
}

export interface PriceGroupPrice {
  id: string;
  priceGroupId: string;
  salesLocationProductId: string;
  price: number;
  salesLocationProduct?: {
    id: string;
    doraProductName: string;
    doraProductNumber: string | null;
    doraProductId: string | null;
    salesLocation: { id: string; name: string; type: string };
  };
  createdAt: string;
  updatedAt: string;
}

export interface PriceGroupInput {
  name: string;
  description?: string | null;
  discountPercent?: number | null;
  validFrom?: string | null;
  validUntil?: string | null;
  customerNumbers?: string[];
}

// Price Engine
export type PriceEngineSuggestionKind = 'PRICE' | 'QTY_TIME';
export type PriceEngineSuggestionSource = 'CUSTOMER' | 'PRICE_GROUP' | 'LOCATION' | 'TIME_TRACKING';
export type PriceEngineSuggestionStatus = 'PENDING' | 'APPROVED' | 'APPLIED' | 'REJECTED' | 'STALE' | 'FAILED';

export interface PriceEngineSuggestion {
  id: string;
  organizationId: string;
  cargoId: string;
  cargoRef: string;
  customerNumber: string | null;
  customerName: string | null;
  salesLocationId: string | null;
  salesLocationName: string | null;
  lineId: string;
  productNumber: string;
  productName: string | null;
  unitName: string | null;
  kind: PriceEngineSuggestionKind;
  source: PriceEngineSuggestionSource;
  currentAmount: string;
  suggestedAmount: string | null;
  currentQty: string;
  suggestedQty: string | null;
  status: PriceEngineSuggestionStatus;
  detectedAt: string;
  decidedAt: string | null;
  appliedAt: string | null;
  errorMessage: string | null;
  matchExplanation: string | null;
}

export interface PriceEngineCustomerPrice {
  id: string;
  organizationId: string;
  customerNumber: string;
  customerName: string | null;
  productNumber: string;
  productName: string | null;
  price: string;
  unitName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PriceEngineCustomerPriceInput {
  customerNumber: string;
  customerName?: string | null;
  productNumber: string;
  productName?: string | null;
  price: number;
  unitName?: string | null;
}

// Dataløn Branche
export type DatalonComputeRule = 'WORK_HOURS' | 'FIXED_PER_PERIOD' | 'MANUAL';
export type DatalonSourceKind = 'ALL' | 'NORMAL' | 'OT1' | 'OT2' | 'EVENING' | 'NIGHT' | 'SICK' | 'HOLIDAY';

export interface DatalonSalaryType {
  id: string;
  organizationId: string;
  lonartNumber: string;
  name: string;
  description: string | null;
  computeRule: DatalonComputeRule;
  sourceKind: DatalonSourceKind;
  defaultRate: string | number;
  fixedAmount: string | number | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DatalonDriverRate {
  id: string;
  organizationId: string;
  driverId: string;
  salaryTypeId: string;
  rate: string | number;
  createdAt: string;
  updatedAt: string;
}

// ============ Emballagekompensation ============

export type EmballageFraktion = 'PAP' | 'PAPIR' | 'PLAST' | 'METAL' | 'GLAS' | 'MDK' | 'TRAE';
export type EmballageBranchGroup = 'INDUSTRI' | 'KONTOR' | 'HANDEL' | 'HOTEL_RESTAURATION_KULTUR';

export interface EmballageFraktionDef {
  fraction: EmballageFraktion;
  eKode: string;
  bilag7: string;
  label: string;
  eakKoder: string[];
  forbehold?: string;
}

export interface EmballageStatus {
  enabled: boolean;
  customerCount: number;
  enrolledCount: number;
  productCount: number;
  facilityCount: number;
}

export interface EmballageCustomer {
  id: string;
  doraCustomerNumber: string;
  customerName: string;
  enrolled: boolean;
  cvr: string | null;
  pNumber: string | null;
  branchGroup: EmballageBranchGroup | null;
  address: string | null;
  postcode: string | null;
  city: string | null;
  note: string | null;
}

export interface EmballageProduct {
  id: string;
  doraProductId: string | null;
  doraProductNumber: string;
  doraProductName: string;
  eKode: string | null;
  fraction: EmballageFraktion | null;
  included: boolean;
  autoMapped: boolean;
}

export interface EmballageFacility {
  id: string;
  name: string;
  cvr: string | null;
  address: string | null;
  postcode: string | null;
  city: string | null;
  contactName: string | null;
  contactEmail: string | null;
  note: string | null;
  isDefault: boolean;
  active: boolean;
}

export interface EmballageLine {
  date: string;
  cargoId: string;
  cargoRef: string;
  customerNumber: string;
  customerName: string;
  productNumber: string;
  productName: string;
  fraction: EmballageFraktion;
  fractionLabel: string;
  eKode: string | null;
  quantity: number;
  unitName: string;
  kg: number;
}

export interface EmballageFractionSum {
  fraction: EmballageFraktion;
  label: string;
  kg: number;
  lineCount: number;
}

export interface EmballageWarning {
  type: 'UNMAPPED_PRODUCT' | 'BAD_UNIT' | 'MISSING_CVR' | 'NO_CUSTOMER';
  message: string;
  cargoRef?: string;
  productNumber?: string;
}

export interface EmballageCustomerBlock {
  customerNumber: string;
  customerName: string;
  cvr: string | null;
  pNumber: string | null;
  branchGroup: string | null;
  address: string | null;
  postcode: string | null;
  city: string | null;
  lines: EmballageLine[];
  perFraction: EmballageFractionSum[];
  totalKg: number;
}

export interface EmballageReportData {
  from: string;
  to: string;
  customers: EmballageCustomerBlock[];
  totals: EmballageFractionSum[];
  totalKg: number;
  lineCount: number;
  cargosScanned: number;
  warnings: EmballageWarning[];
}

export type EmballageReportStatus = 'PENDING' | 'DONE' | 'FAILED';

export interface EmballageSavedReport {
  id: string;
  facilityName: string;
  periodFrom: string;
  periodTo: string;
  filename: string;
  totalKg: number;
  lineCount: number;
  customerCount: number;
  generatedAt: string;
  generatedBy: string | null;
  status: EmballageReportStatus;
  error: string | null;
  // Kun med på enkeltopslaget, og kun når status er DONE.
  data?: EmballageReportData | null;
}
